/**
 * How much of one payment source is refunded and how much is still charged. The payment report and
 * account history both read these, so the two screens always agree. The rows come from the API's
 * per-source `transaction`: refund and chargeback amounts arrive negative.
 */

import { canRefundSource, ReportTransaction } from '@/utils/paymentRowActions'

export interface SourceMoneyRow {
  amount?: number | string | null
  status?: string
  processed_time?: string | null
}

export interface SourceTransaction extends ReportTransaction {
  amount?: number | string | null
  convenience_fee?: number | string | null
  refunds?: SourceMoneyRow[]
  chargebacks?: SourceMoneyRow[]
}

export interface SourceWithTransaction {
  transaction?: SourceTransaction | null
}

/**
 * A money value from the API, which sends decimal text or numbers.
 * @param value - The amount.
 * @returns The number, or 0 when it is missing or not a number.
 */
export const toNumber = (value: number | string | null | undefined): number =>
  parseFloat(String(value ?? 0)) || 0

/**
 * Total of the source's successful refunds.
 * @param source - A payment source with its transaction.
 * @returns The total as a negative number, or 0 when nothing was refunded.
 */
export function sourceRefundAmount(source?: SourceWithTransaction): number {
  return (source?.transaction?.refunds ?? []).reduce(
    (sum, refund) =>
      refund?.status === 'OK' ? sum + toNumber(refund.amount) : sum,
    0
  )
}

/**
 * Total of the source's chargebacks.
 * @param source - A payment source with its transaction.
 * @returns The total as a negative number, or 0 when there are none.
 */
export function sourceChargebackAmount(source?: SourceWithTransaction): number {
  return (source?.transaction?.chargebacks ?? []).reduce(
    (sum, chargeback) => sum + toNumber(chargeback?.amount),
    0
  )
}

/**
 * What is still charged on the source: amount plus fee, less refunds and chargebacks.
 * @param source - A payment source with its transaction.
 * @returns The remaining amount.
 */
export function sourceRemainingAmount(source?: SourceWithTransaction): number {
  const transaction = source?.transaction

  return (
    toNumber(transaction?.amount) +
    toNumber(transaction?.convenience_fee) +
    sourceRefundAmount(source) +
    sourceChargebackAmount(source)
  )
}

/**
 * Whether a source is a bank (ACH) debit. Its refunds go through Support, so its button is disabled.
 * @param source - A payment source with its transaction.
 * @returns True for an ACH source.
 */
export function isAchSource(source?: {
  transaction?: { type?: string } | null
}): boolean {
  return source?.transaction?.type === 'ACH'
}

/**
 * Whether a source can take a refund: the API allows it and money is still charged on it.
 * @param source - A payment source with its transaction.
 * @returns True when the per-card Refund button may show, permissions aside.
 */
export function hasRefundableBalance(source?: SourceWithTransaction): boolean {
  // In whole cents, as the refund popup offers it: a float residue is not money left.
  return (
    canRefundSource(source?.transaction ?? undefined) &&
    Math.round(sourceRemainingAmount(source) * 100) > 0
  )
}

/**
 * Whether a payment's receipt may be downloaded or sent.
 * @param payment - The payment, with the API's `status`.
 * @returns True for a paid, refunded or partially refunded payment, of any origin.
 */
export function isReceiptAvailable(
  payment?: { status?: string } | null
): boolean {
  // A partially refunded payment is stored as REFUND; display_status is only its label.
  return ['PAID', 'REFUND'].includes(
    String(payment?.status ?? '').toUpperCase()
  )
}

/**
 * A payment's refunded total: every successful refund on every card, added up.
 * @param sources - The payment's sources, each with its transaction.
 * @returns The total as a positive number.
 */
export function paymentRefundedTotal(
  sources?: SourceWithTransaction[]
): number {
  return (sources ?? []).reduce(
    (sum, source) => sum - sourceRefundAmount(source),
    0
  )
}

/**
 * When the payment's latest successful refund was processed.
 * @param sources - The payment's sources, each with its transaction.
 * @returns The ISO timestamp, or null when no refund has a processed time.
 */
export function latestRefundTime(
  sources?: SourceWithTransaction[]
): string | null {
  const times = (sources ?? [])
    .flatMap(source => source?.transaction?.refunds ?? [])
    .filter(refund => refund?.status === 'OK' && refund.processed_time)
    .map(refund => String(refund.processed_time))
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

  return times.length ? times[times.length - 1] : null
}
